import type { HTMLAttributes } from 'react';

import type { ProductTagType } from 'models/product';
import { cnm } from 'utils/classNames';
import { is } from 'utils/helpers';

export type Size = 'lg' | 'md' | 'sm';

interface Props extends HTMLAttributes<HTMLParagraphElement> {
	type: ProductTagType;
	className?: string;
	size: Size;
	text: string;
}

export default function ProductTag({
	className,
	size,
	text,
	type,
	...attrs
}: Props) {
	// The design system has flags and tags as separate components, but it's
	// unified here since the backend model for tag/flag data is the same.
	const isFlag = is.oneOf(type, 'Outlet', 'Sale');
	// Flag without the speech bubble style.
	const isFlagBadge = type === 'News';
	const isTag = !isFlag && !isFlagBadge;

	return (
		<p
			{...attrs}
			className={cnm(
				'inline-block align-top font-bold',

				// Colors
				type === 'Upsale' && 'bg-brand text-inverse',
				type === 'TestWinner' && 'bg-error text-error',
				type === 'QtyBasedOffer' && 'bg-base text-base',
				type === 'Bundle' && 'bg-inverse text-inverse',
				type === 'News' && 'bg-news text-news',
				is.oneOf(type, 'JulaHero', 'Outlet', 'Sale') &&
					'bg-pricetagCampaign text-pricetagCampaign',

				isFlag && [
					'relative rounded-xs rounded-bl-none',
					'after:absolute after:top-full after:left-0 after:size-2 after:bg-pricetagCampaign after:[clip-path:polygon(0_0,0_100%,100%_0)]',
					'forced-colors:border forced-colors:after:hidden',

					size === 'sm' && 'px-2 py-0.5 text-sm',
					size === 'md' && 'px-2 py-0.5 text-md md:text-lg',
					size === 'lg' && 'px-3 py-1.5 font-heading text-h5',
				],
				isFlagBadge && [
					'forced-colors:border',

					size === 'sm' && 'rounded-xs px-2 py-0.5 text-sm',
					size === 'md' && 'rounded-sm px-2 py-0.5 text-md md:text-lg',
					size === 'lg' && 'rounded-md px-3 py-1.5 font-heading text-h5',
				],
				isTag && [
					// Needs a border for forced colors mode. Always include it for
					// consistent sizing and set it to transparent for the types that
					// don't have a border.
					'border',
					is.oneOf(type, 'Upsale', 'TestWinner', 'JulaHero') &&
						'border-transparent',
					is.oneOf(type, 'Bundle', 'QtyBasedOffer') && 'border-strong',

					size === 'sm' && 'px-1 py-px text-xs',
					size === 'md' && 'px-2 py-0.5 text-sm',
					size === 'lg' && 'px-2 py-0.75 text-md md:text-lg',
				],

				className,
			)}
		>
			{text}
		</p>
	);
}
ProductTag.displayName = 'ProductTag';
